import dayjs, { Dayjs } from "dayjs"
import { IReportEntry, IReportEntryDay, ReportEntry, ReportEntryDay } from "../models/report"

type DateInput = Date | string | null | undefined

// The days a user may add to an entry: the report's start through end date.
// Null when the report has no usable range yet.
export const addableDayRange = (start: DateInput, end: DateInput): { min: Dayjs; max: Dayjs } | null => {
    if (!start || !end) return null
    const min = dayjs(start).startOf("day")
    const max = dayjs(end).startOf("day")
    if (!min.isValid() || !max.isValid() || min.isAfter(max)) return null
    return { min, max }
}

// Days flagged for destruction stay in the form state until the save
// round-trips, so their dates are free to be added again.
export const isDayTaken = (date: Dayjs, days: IReportEntryDay[]): boolean =>
    days.some(d => !d._destroy && dayjs(d.entry_date).isSame(date, "day"))

export const firstAddableDay = (days: IReportEntryDay[], start: DateInput, end: DateInput): Dayjs | null => {
    const range = addableDayRange(start, end)
    if (!range) return null
    for (let date = range.min; !date.isAfter(range.max); date = date.add(1, "day")) {
        if (!isDayTaken(date, days)) return date
    }
    return null
}

// Days without a date sort first, as the form always did.
export const compareByEntryDate = (
    a: Pick<IReportEntryDay, "entry_date">,
    b: Pick<IReportEntryDay, "entry_date">,
): number => {
    if (!a.entry_date) return -1
    if (!b.entry_date) return 1
    return dayjs(a.entry_date).valueOf() - dayjs(b.entry_date).valueOf()
}

// A removed day only needs its id to be destroyed; sending its events would
// get them validated for a row that is going away. Unsaved ones just vanish.
export const tombstoneDestroyedDays = (days: IReportEntryDay[]): Partial<IReportEntryDay>[] =>
    days
        .filter(d => !d._destroy || d.id > 0)
        .map(d => (d._destroy ? { id: d.id, _destroy: true } : d))

// An entry with one empty day per date of the report's range. Built from
// plain objects: react-hook-form only clones those, and a class instance in
// the form state is shared by reference, so watchers of its arrays (e.g. the
// Add Day button) would never see a day being flagged for removal.
export const newEntryWithDays = (start: DateInput, end: DateInput): IReportEntry => {
    const from = dayjs(start)
    const count = dayjs(end).diff(from, "day")
    const days: IReportEntryDay[] = []
    for (let i = 0; i <= count; i++) {
        days.push({ ...new ReportEntryDay({ entry_date: from.add(i, "day").toDate() }) })
    }
    return { ...new ReportEntry(), report_entry_days: days }
}
